import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { CalendarBlank, X } from "@phosphor-icons/react";
import {
  FY_MIN, currentFYStart, fyLabel, getFYRange, setFYRangeLocal,
  hasStoredFYRange, fyRangeLabel,
} from "@/lib/fyRange";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FYSelector = ({ user }) => {
  const [open, setOpen] = useState(false);
  const range = getFYRange();
  const [fromYear, setFromYear] = useState(range.from_year);
  const [toYear, setToYear] = useState(range.to_year);
  const [saving, setSaving] = useState(false);

  // Cross-device persistence: adopt the range saved on the user profile
  // when this browser has no local selection yet.
  useEffect(() => {
    if (user?.fy_range && !hasStoredFYRange()) {
      setFYRangeLocal(user.fy_range);
      window.location.reload();
    }
  }, [user]);

  const cur = currentFYStart();
  const years = [];
  for (let y = cur + 1; y >= FY_MIN; y--) years.push(y);

  const apply = async (fr, to) => {
    const f = Math.min(fr, to), t = Math.max(fr, to);
    setSaving(true);
    setFYRangeLocal({ from_year: f, to_year: t });
    try { await axios.put(`${API}/auth/fy-range`, { from_year: f, to_year: t }); } catch {}
    window.location.reload();
  };

  return (
    <>
      <div className="px-3 sm:px-4 pt-3">
        <button
          onClick={() => { setFromYear(range.from_year); setToYear(range.to_year); setOpen(true); }}
          data-testid="fy-selector-btn"
          title="Select Financial Year (1 April - 31 March)"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 transition-colors"
        >
          <CalendarBlank size={16} weight="duotone" />
          <span className="text-xs font-black tracking-wide" data-testid="fy-selector-label">{fyRangeLabel(range)}</span>
        </button>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4" data-testid="fy-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md border border-slate-200 shadow-2xl w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-black text-slate-900">Financial Year Range</h2>
              <button onClick={() => setOpen(false)} data-testid="fy-modal-close" className="p-1.5 text-slate-500 hover:text-slate-900 rounded-sm hover:bg-slate-100"><X size={20} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-6">Only data within the selected range (1 April to 31 March) is shown across the entire software. This setting stays saved even after logout.</p>

            <div className="flex items-end gap-3 mb-5">
              <div className="flex-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">From FY</label>
                <select value={fromYear} onChange={(e) => setFromYear(Number(e.target.value))} data-testid="fy-from-select"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm font-semibold bg-white">
                  {years.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                </select>
              </div>
              <div className="pb-3 text-slate-400 font-black">→</div>
              <div className="flex-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">To FY</label>
                <select value={toYear} onChange={(e) => setToYear(Number(e.target.value))} data-testid="fy-to-select"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm font-semibold bg-white">
                  {years.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <button onClick={() => { setFromYear(cur); setToYear(cur); }} data-testid="fy-current-btn"
                className="px-4 py-2 text-xs font-bold border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-100">
                Current FY ({fyLabel(cur)})
              </button>
              <button onClick={() => { setFromYear(FY_MIN); setToYear(cur); }} data-testid="fy-all-btn"
                className="px-4 py-2 text-xs font-bold border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-100">
                All Years ({fyLabel(FY_MIN)} to {fyLabel(cur)})
              </button>
            </div>

            <button onClick={() => apply(fromYear, toYear)} disabled={saving} data-testid="fy-apply-btn"
              className="w-full px-4 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-sm font-black disabled:opacity-60">
              {saving ? "Saving..." : `Apply — ${fyRangeLabel({ from_year: Math.min(fromYear, toYear), to_year: Math.max(fromYear, toYear) })}`}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
